import { useState } from 'react';
import styled from 'styled-components';
import { Button, BUTTON_VARIANTS } from 'ui/button';
import { colors } from 'ui/theme';
import { useDispatch, useSelector } from 'react-redux';
import { addNegativeToastWithErrorId } from 'modules/toastContainer';
import { useValidateWorkflow, NodeError } from 'modules/workflows/hooks';
import { useJourneyReviewContext } from 'modules/workflows/context/JourneyReviewContext';
import {
  addNodeErrors,
  getIsJourneyReviewPanelEnabled,
  updateIsAsyncLoading,
} from '../../../ducks';

const StyledButtonText = styled.span`
  color: ${colors.green100};
`;

export const SEND_TEST_TRIGGER_TEST_ID = 'action-bar-send-test-trigger';
export const PUBLISH_TEST_ID = 'action-bar-publish';

export const ValidatedWorkflowButton = ({
  onValidationSuccess,
  buttonText,
  isDisabled = false,
  ...props
}: {
  onValidationSuccess: () => void;
  buttonText: string;
  isDisabled?: boolean;
}) => {
  const dispatch = useDispatch();
  const {
    setIssues,
    setIsOpen: setIsJourneyReviewOpen,
    getOptimizations,
  } = useJourneyReviewContext();
  const isJourneyReviewPanelEnabled = useSelector(getIsJourneyReviewPanelEnabled);

  const [isValidating, setIsValidating] = useState(false);

  const onSuccess = () => {
    onValidationSuccess();
    setIsValidating(false);
  };

  const onError = (validationErrors: NodeError[] = []) => {
    if (validationErrors.length) {
      // Store the validation errors on the Journey Review Context so we can have access to them in the Journey Review
      // I think this will be a tempory solution and we should move the use useValidateWorkflook to pass in the callback for OnSuccess and onError when we call validatedWorkflow.
      if (isJourneyReviewPanelEnabled) {
        setIssues(validationErrors);
        getOptimizations();
        setIsJourneyReviewOpen(true);
      } else {
        const nodeIds = validationErrors.map((x: NodeError) => x?.data?.nodeId.toString());
        dispatch(addNodeErrors(nodeIds));
      }

      const draftErrors = validationErrors.filter(
        (err) => err.message === 'Draft journey is still attached to a Send to Journey node.',
      );
      if (draftErrors.length) {
        dispatch(
          addNegativeToastWithErrorId(
            'Some of your linked journeys aren’t published yet. You’ll need to publish those journeys before you can publish this one.',
            'LinkedJourneyNotPublished',
          ),
        );
      }
      if (draftErrors.length !== validationErrors.length) {
        dispatch(
          addNegativeToastWithErrorId(
            'You need to finish setting up some nodes before you can test/publish this journey.',
            'FinishSettingUpNodes',
          ),
        );
      }
    } else {
      dispatch(
        addNegativeToastWithErrorId(
          'Sorry, but an unexpected error has occurred.',
          'UnexpectedError',
        ),
      );
    }
    setIsValidating(false);
  };

  const { validateWorkflow, isLoading } = useValidateWorkflow({
    onValidationError: onError,
    onValidationSuccess: onSuccess,
  });

  const text = isValidating ? 'Validating...' : buttonText;

  const OnClick = () => {
    setIsValidating(true);
    dispatch(updateIsAsyncLoading(true));

    validateWorkflow();
  };

  return (
    <Button
      {...props}
      variant={BUTTON_VARIANTS.SECONDARY}
      onClick={OnClick}
      isDisabled={isDisabled || isLoading || isValidating}
    >
      <StyledButtonText>{text}</StyledButtonText>
    </Button>
  );
};

export const PublishButton = ({
  handlePublish,
  isDisabled,
}: {
  handlePublish: () => void;
  isDisabled: boolean;
}) => (
  <ValidatedWorkflowButton
    data-test={PUBLISH_TEST_ID}
    buttonText="Publish"
    onValidationSuccess={handlePublish}
    isDisabled={isDisabled}
  />
);

export const TestWorkflowButton = ({
  handleTestWorkflow,
  isDisabled,
}: {
  handleTestWorkflow: () => void;
  isDisabled: boolean;
}) => (
  <ValidatedWorkflowButton
    data-test={SEND_TEST_TRIGGER_TEST_ID}
    buttonText="Test Journey"
    onValidationSuccess={handleTestWorkflow}
    isDisabled={isDisabled}
  />
);
